/**
 * Right Content Drawer - Base Component
 * 
 * Reusable drawer that slides from right to left
 * Can be used across the application for different content types
 * 
 * Features:
 * - Configurable initial width (default 572px)
 * - Resizable by dragging the left edge
 * - Responsive (full screen on mobile)
 * - Custom footer support
 * - Slides from right to left
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Drawer } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import styled from 'styled-components';

// ========== TYPE DEFINITIONS ==========

export interface RightContentDrawerProps {
  open: boolean;
  title?: string;
  width?: number | string;
  minWidth?: number;
  maxWidth?: number;
  children: React.ReactNode;
  footer?: React.ReactNode;
  onClose: () => void;
  closable?: boolean;
  maskClosable?: boolean;
  className?: string;
  resizable?: boolean;
}

// ========== STYLED COMPONENTS ==========

const StyledDrawer = styled(Drawer)<{ $resizable?: boolean }>`
  .ant-drawer-header {
    padding: 20px 24px;
    border-bottom: none;
    background: #FFFFFF !important; 
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .ant-drawer-header-title {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .ant-drawer-title {
    font-family: Inter;
    font-weight: 500;
    font-size: 18px;
    line-height: 100%;
    color: #000000;
    flex: 1;
  }

  .ant-drawer-close {
    order: 2;
    margin: 0;
    padding: 0;
  }

  .ant-drawer-body {
    padding: 24px;
    background: #FFFFFF;
  }

  .ant-drawer-footer {
    padding: 16px 24px;
    background: #FFFFFF;
    border-top: none;
  }

  @media (max-width: 768px) {
    .ant-drawer-content-wrapper {
      width: 100% !important;
    }
  }
`;

// Fixed position resize handle - invisible but functional
// Uses cursor change to indicate resizability
const ResizeHandle = styled.div<{ $left: number }>`
  position: fixed;
  left: ${props => props.$left}px;
  top: 0;
  bottom: 0;
  width: 6px;
  cursor: ew-resize;
  background: transparent;
  z-index: 1001;
`;

// ========== MAIN COMPONENT ==========

const RightContentDrawer: React.FC<RightContentDrawerProps> = ({
  open,
  title,
  width = 572,
  minWidth = 320,
  maxWidth = window.innerWidth * 0.9,
  children,
  footer,
  onClose,
  closable = true,
  maskClosable = true,
  className,
  resizable = true,
}) => {
  const initialWidth = typeof width === 'number' ? width : parseInt(width as string, 10) || 572;
  const [currentWidth, setCurrentWidth] = useState<number>(initialWidth);
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef<{ startX: number; startWidth: number } | null>(null);

  // Calculate left position for fixed resize handle (drawer is positioned from right)
  const resizeHandleLeft = window.innerWidth - currentWidth;

  // Reset width when drawer opens
  useEffect(() => {
    if (open) {
      setCurrentWidth(initialWidth);
    }
  }, [open, initialWidth]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    resizeRef.current = {
      startX: e.clientX,
      startWidth: currentWidth,
    };
  }, [currentWidth]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!resizeRef.current) return;

    const deltaX = resizeRef.current.startX - e.clientX;
    let newWidth = resizeRef.current.startWidth + deltaX;

    // Clamp width between min and max
    newWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));
    setCurrentWidth(newWidth);
  }, [minWidth, maxWidth]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    resizeRef.current = null;
  }, []);

  // Add/remove global mouse event listeners
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  return (
    <>
      {/* Fixed position resize handle - outside drawer DOM */}
      {open && resizable && (
        <ResizeHandle 
          $left={resizeHandleLeft}
          className={isResizing ? 'resizing' : ''}
          onMouseDown={handleMouseDown}
        />
      )}
      <StyledDrawer
        title={title}
        placement="right"
        width={currentWidth}
        open={open}
        onClose={onClose}
        closeIcon={closable ? <CloseOutlined style={{ fontSize: 16 }} /> : null}
        closable={closable}
        maskClosable={maskClosable}
        footer={footer}
        className={className}
        $resizable={resizable}
      >
        {children}
      </StyledDrawer>
    </>
  );
};

export default RightContentDrawer;

